import { onScopeDispose, ref } from 'vue'

import { QueueRabbitClient } from '@collector/queue-gateway-shared-client'
import { ClientErrorPayload } from '@collector/queue-gateway-shared-types'
import { NetworkError } from '@collector/shared-utils'
import { useNotifications } from '@desktop/globalState/notifications'
import { useAuth } from '@desktop/globalState/userAuth/useAuth'
import { handleError } from '@desktop/utils/errorHandler'
import { createSharedComposable } from '@vueuse/core'

export const useQueueClient = createSharedComposable(() => {
  const { tokens, refreshAuth, logOut, accessTokenBus } = useAuth()
  const { addSuccessNotification, addErrorNotification } = useNotifications()

  const client = new QueueRabbitClient({
    url: import.meta.env.VITE_QUEUE_GATEWAY_URL as string,
    auth: { accessToken: tokens?.accessToken ?? '' },
  })

  accessTokenBus.on((accessToken) => client.setAuth({ accessToken }))

  const connected = ref(false)

  client.onConnect(() => {
    connected.value = true
  })
  client.onDisconnect(() => {
    connected.value = false
  })

  let problemNotified = false
  client.onStatusChange((isConnected) => {
    connected.value = isConnected
    if (!isConnected && navigator.onLine) {
      addErrorNotification('Websocket connection problem')
      problemNotified = true
    } else if (isConnected && problemNotified) {
      addSuccessNotification('Websocket connection restored')
      problemNotified = false
    }
  })

  const errorHandlers = {
    JwtExpiredError: async () => {
      const refreshed = await refreshAuth()
      client.setAuth({ accessToken: refreshed.accessToken })
    },
    JwtError: (error: ClientErrorPayload['errors'][number]) => {
      logOut()
      handleError(new Error(`JWT error: ${error.message}`, { cause: error }))
    },
    UnauthorizedError: (error: ClientErrorPayload['errors'][number]) => {
      logOut()
      handleError(
        new NetworkError('Unauthorized access: please log in again', {
          cause: error,
        }),
      )
    },
    UnknownError: (error: ClientErrorPayload['errors'][number]) => {
      handleError(
        new Error(`Queue Gateway error: ${JSON.stringify(error)}`, {
          cause: error,
        }),
      )
    },
  }

  client.onClientError(async (clientError: ClientErrorPayload) => {
    for (const error of clientError.errors) {
      await errorHandlers[error.type](error)
    }
  })

  onScopeDispose(() => client.destroy())

  return {
    connected,
    client,
  }
})
